import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../../api/api';
import { useMap } from '../../features/mapping/MapContext';
import { sampleGridTerrain } from '../../features/mapping/utils/sampleGridTerrain';
import InfoPanel from '../../features/mapping/InfoPanel/InfoPanel';
import LayoutEditorControls from '../LayoutEditorControls/LayoutEditorControls';
import './BottomPanel.css';
import DataLayerTabs from '../../features/mapping/DataLayerTabs/DataLayerTabs';
import ErrorBoundary from '../../features/mapping/ErrorBoundary/ErrorBoundary';

const BottomPanel = ({
  initialLayers = {},
  visible = true,
  editMode = false,
  showPorts: externalShowPorts,
  setShowPorts: externalSetShowPorts,
  showColdStorage: externalShowColdStorage,
  setShowColdStorage: externalSetShowColdStorage,
  showStateBoundaries: externalShowStateBoundaries,
  setShowStateBoundaries: externalSetShowStateBoundaries,
  showBottomTemp: externalShowBottomTemp,
  setShowBottomTemp: externalSetShowBottomTemp,
  trawlCatches: externalTrawlCatches,
  setTrawlCatches: externalSetTrawlCatches,
  onSpeciesLEKLoad,
  onToggleSpeciesLEKData,
  speciesLEKData = null,
  pageType = null,
  resourceId = null
}) => {
  const { setCesiumView, viewerReady, viewer, layerController } = useMap();

  // ============================================================================
  // MAP PAGE FUNCTIONALITY - All state and handlers from Map.jsx
  // ============================================================================
  
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [connectionData, setConnectionData] = useState(null);

  // Shared species state
  const [species, setSpecies] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState('');
  const [speciesLoading, setSpeciesLoading] = useState(false);

  // Bottom Temperature state (use external if provided, otherwise manage locally)
  const [localShowBottomTemp, setLocalShowBottomTemp] = useState(false);
  const [bottomTempLoading, setBottomTempLoading] = useState(false);
  
  const showBottomTemp = externalShowBottomTemp !== undefined ? externalShowBottomTemp : localShowBottomTemp;
  const setShowBottomTemp = externalSetShowBottomTemp ? externalSetShowBottomTemp : setLocalShowBottomTemp;

  // Unified data state
  const [habitatData, setHabitatData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [monthRange, setMonthRange] = useState({ start: 12, end: 2 }); // initialize to "None" (no data)
  const [statistics, setStatistics] = useState({});

  // Popup state management
  const [activePopup, setActivePopup] = useState(null); // 'migration-heatmap' | 'hourly-catchability' | '3d-movement' | 'port' | null
  const [selectedPort, setSelectedPort] = useState(null);
  
  // Catchability state
  const [catchabilityData, setCatchabilityData] = useState(null);
  const [catchabilityLoading, setCatchabilityLoading] = useState(false);
  const [lekStatus, setLekStatus] = useState(null);

  // Trawl survey catches state - use external if provided, otherwise manage locally
  const [localTrawlCatches, setLocalTrawlCatches] = useState(null);
  const trawlCatches = externalTrawlCatches !== undefined ? externalTrawlCatches : localTrawlCatches;
  const setTrawlCatches = externalSetTrawlCatches || setLocalTrawlCatches;
  
  // Stock summary state
  const [showStockSummaries, setShowStockSummaries] = useState(false);
  const [stockSummaryData, setStockSummaryData] = useState([]);
  const [stockSummaryLoading, setStockSummaryLoading] = useState(false);
  const [timeOfDayFilter, setTimeOfDayFilter] = useState(''); // 'Nighttime', 'Daytime', 'Dusk', 'Dawn', or ''
  const [depthRange, setDepthRange] = useState({ min: 0, max: 200 }); // Depth range in meters
  const [tempDepthRange, setTempDepthRange] = useState({ min: 0, max: 200 }); // Temporary range for slider interaction
  const [botTempRange, setBotTempRange] = useState({ min: -10, max: 30 }); // Bottom temperature range in Celsius
  const [tempBotTempRange, setTempBotTempRange] = useState({ min: -10, max: 30 }); // Temporary range for slider interaction
  const [bottomTypeFilter, setBottomTypeFilter] = useState(''); // 'Sand', 'Mud', 'Gravel', or ''

  // Ports, Cold Storage, and State Boundaries state (use external if provided, otherwise manage locally)
  const [localShowPorts, setLocalShowPorts] = useState(false);
  const [localShowColdStorage, setLocalShowColdStorage] = useState(false);
  const [localShowStateBoundaries, setLocalShowStateBoundaries] = useState(false);
  
  // Use external state if provided, otherwise use local state
  const showPorts = externalShowPorts !== undefined ? externalShowPorts : localShowPorts;
  const setShowPorts = externalSetShowPorts ? externalSetShowPorts : setLocalShowPorts;
  const showColdStorage = externalShowColdStorage !== undefined ? externalShowColdStorage : localShowColdStorage;
  const setShowColdStorage = externalSetShowColdStorage ? externalSetShowColdStorage : setLocalShowColdStorage;
  const showStateBoundaries = externalShowStateBoundaries !== undefined ? externalShowStateBoundaries : localShowStateBoundaries;
  const setShowStateBoundaries = externalSetShowStateBoundaries ? externalSetShowStateBoundaries : setLocalShowStateBoundaries;

  const [movementAnalysisMode, setMovementAnalysisMode] = useState(false);
  const [movementAnalysisData, setMovementAnalysisData] = useState(null);

  // Popup position for LEK/port popups (Cesium screen coords from click, passed to MapController)
  const [lekPopupScreenPosition, setLekPopupScreenPosition] = useState(null);
  const [lekPopupContainer, setLekPopupContainer] = useState(null);

  // Fleet animation data state (reusing existing session state)
  const [fleetMapData, setFleetMapData] = useState([]);
  const [fleetDataLoading, setFleetDataLoading] = useState(false);
  const [fleetAnimationTrigger, setFleetAnimationTrigger] = useState(0);
  
  // Session management state (needed before functions that use it)
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);

  // Resizable panel height state (in viewport height units, e.g., 0.4 for 40vh)
  const [panelHeight, setPanelHeight] = useState(0.4); // Default to 40vh
  const [isDragging, setIsDragging] = useState(false);
  const [hasActiveTab, setHasActiveTab] = useState(false);
  const dragStartY = useRef(null);
  const dragStartHeight = useRef(null);

  // Get all API functions from the useApi hook - ONLY MAP/LEK FUNCTIONS
  const {
    getTripResults,
    getSessions,
    // Map/LEK API functions
    checkLekConnectionHealth,
    getLekSpecies,
    getLekStatistics,
    getLekHabitatData,
    getLekGridCellDetails,
    getLekCatchabilityData,
    getStockSummary,
    getTrawlSurveyCruises,
    getTrawlSurveyCatchesByCruise
  } = useApi();

  // Helper function for getting selected species name
  const getSelectedSpeciesNameForMap = useCallback(() => {
    const selectedSpeciesObj = species.find(s => s.species_id.toString() === selectedSpecies);
    return selectedSpeciesObj?.common_name || selectedSpeciesObj?.species_name || 'Unknown Species';
  }, [species, selectedSpecies]);

  // Load habitat data when dependencies change (with request cancellation)
  const refreshGridData = useCallback(async () => {
    if (!selectedSpecies) return;
    
    // Check if "None" is selected (invalid month range)
    if (monthRange.start === 0 && monthRange.end === 0) {
      console.log('No season selected, clearing habitat data');
      setHabitatData([]);
      setDataLoading(false);
      return;
    }
    
    setDataLoading(true);
    
    const params = {
      species: parseInt(selectedSpecies),
      month_start: monthRange.start,
      month_end: monthRange.end
    };
    
    console.log('Fetching habitat data with params:', params);
    
    // Create abort controller for cancellation
    const abortController = new AbortController();
    
    try {
      const response = await getLekHabitatData(params.species, { start: params.month_start, end: params.month_end });
      const data = response.data;
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        console.log('Habitat data request was cancelled');
        return;
      }
      
      console.log('Habitat data loaded:', data.length, 'cells');
      setHabitatData(data);

      function getDateRangeFromMonths(startMonth, endMonth) {
        const today = new Date();
        let year = today.getFullYear();

        // First day of start month
        let startDate = new Date(year, startMonth - 1, 1);
        // Last day of end month
        let endDate = new Date(year, endMonth, 0);

        // If the range ends in the future, roll back one year
        if (endDate > today) {
          year -= 1;
          startDate = new Date(year, startMonth - 1, 1);
          endDate = new Date(year, endMonth, 0);
        }

        if (startDate > endDate) {
          startDate = new Date(year-1, startMonth - 1, 1);
        }

        // Format as YYYY-MM-DD
        const formatDate = (date) => {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, "0");
          const d = String(date.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        };

        return { 
          startDate: formatDate(startDate), 
          endDate: formatDate(endDate) 
        };
      }

      const { startDate, endDate } = getDateRangeFromMonths(monthRange.start, monthRange.end);

      const catchabilityResult = await getLekCatchabilityData(params.species, startDate, endDate);
      console.log('Catchability result:', catchabilityResult);
      // Handle catchability results
      if (catchabilityResult.status===200 && catchabilityResult.data.success) {
        setCatchabilityData(catchabilityResult.data);
        console.log('Temporal catchability loaded');
      } else {
        console.warn('Catchability failed:', catchabilityResult.reason);
        setCatchabilityData(null);
      }

      // Species LEK data loading is now handled by parent component (Stock.jsx) via onSpeciesLEKLoad callback
      if (onSpeciesLEKLoad) {
        onSpeciesLEKLoad(params.species);
      }

      setDataLoading(false);
      // Update connection status when API call succeeds
      if (connectionStatus !== 'connected') {
        setConnectionStatus('connected');
        setConnectionData({ connected: true, api_health: true });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Habitat data request was cancelled');
        return;
      }
      console.error('Habitat data fetch failed:', error);
      setDataLoading(false);
      // Don't change connection status here since it might be a temporary error
    }
    
    // Return cleanup function
    return () => {
      abortController.abort();
    };
  }, [selectedSpecies, monthRange, getLekHabitatData, connectionStatus, getLekCatchabilityData, onSpeciesLEKLoad]);

  // REMOVED: Automatic habitat data loading
  // Habitat data is now controlled via MapController and only loads when lekHabitat layer is active
  // useEffect(() => {
  //   refreshGridData();
  // }, [refreshGridData]);

  // Load stock summary data
  const loadStockSummaryData = useCallback(async () => {
    console.log('🔄 loadStockSummaryData called:', { showStockSummaries, timeOfDayFilter, depthRange, bottomTypeFilter });
    
    if (!showStockSummaries) {
      console.log('❌ Skipping stock summary load: checkbox not checked');
      return;
    }
    
    setStockSummaryLoading(true);
    try {
      const filters = [];
      if (timeOfDayFilter) filters.push(`time: ${timeOfDayFilter}`);
      if (depthRange.min > 0 || depthRange.max < 200) filters.push(`depth: ${depthRange.min}-${depthRange.max} meters`);
      if (bottomTypeFilter) filters.push(`bottom: ${bottomTypeFilter}`);
      
      console.log('📡 Fetching stock summary data for year 2024...', filters.length ? `with filters: ${filters.join(', ')}` : '');
      const response = await getStockSummary(2024, timeOfDayFilter || null, depthRange.min, depthRange.max, bottomTypeFilter || null);
      const data = response.data || [];
      console.log('📊 Raw stock summary data received:', data.length, 'records');
      
      // Filter only for station IDs above 420 (no species filtering)
      const filteredData = data.filter(record => record.station_id > 420);
      
      console.log('✅ Stock summary data loaded:', filteredData.length, 'stations (all species)');
      console.log('📍 Sample filtered data:', filteredData.slice(0, 3));
      setStockSummaryData(filteredData);
    } catch (error) {
      console.error('❌ Failed to load stock summary data:', error);
      setStockSummaryData([]);
    } finally {
      setStockSummaryLoading(false);
    }
  }, [showStockSummaries, timeOfDayFilter, depthRange, bottomTypeFilter, getStockSummary]);

  // Load stock summary data when checkbox is toggled or filters change
  // Only load if showStockSummaries is true
  useEffect(() => {
    if (showStockSummaries) {
      loadStockSummaryData();
    }
  }, [showStockSummaries, timeOfDayFilter, depthRange, bottomTypeFilter, getStockSummary]);

  // Load fleet animation data using session (reuse existing session state)
  const loadFleetDataForMap = useCallback(async () => {
    if (!selectedSessionId) {
      console.log('❌ No session selected for fleet data');
      setFleetMapData([]);
      return;
    }

    console.log('🔄 Loading fleet animation data for session:', selectedSessionId);
    setFleetDataLoading(true);
    
    try {
      // Load trip results with session filter
      const response = await getTripResults({ 
        session_id: selectedSessionId,
        limit: 100 
      });
      
      let trips = [];
      if (response?.data?.success && response?.data?.results) {
        trips = response.data.results;
      } else if (Array.isArray(response?.data)) {
        trips = response.data;
      }
      
      // Filter for valid coordinates
      const validTripData = trips.filter(trip => 
        trip.port_latitude && trip.port_longitude && 
        trip.target_area_latitude && trip.target_area_longitude &&
        !isNaN(trip.port_latitude) && !isNaN(trip.port_longitude) &&
        !isNaN(trip.target_area_latitude) && !isNaN(trip.target_area_longitude)
      );
      
      console.log('✅ Fleet data loaded:', validTripData.length, 'valid trips from', trips.length, 'total trips');
      setFleetMapData(validTripData);
    } catch (error) {
      console.error('❌ Failed to load fleet data:', error);
      setFleetMapData([]);
    } finally {
      setFleetDataLoading(false);
    }
  }, [getTripResults, selectedSessionId]);

  // Load fleet data when session changes
  useEffect(() => {
    if (selectedSessionId) {
      loadFleetDataForMap();
    } else {
      setFleetMapData([]);
    }
  }, [loadFleetDataForMap, selectedSessionId]);

  // Debounced effect for depth range changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (tempDepthRange.min !== depthRange.min || tempDepthRange.max !== depthRange.max) {
        setDepthRange(tempDepthRange);
      }
    }, 500); // 500ms delay after user stops interacting

    return () => clearTimeout(timeoutId);
  }, [tempDepthRange, depthRange]);

  // Apply trawl-survey component_default_values to filter state when layout includes them
  useEffect(() => {
    const trawlDefaults = initialLayers?.['trawl-survey'];
    if (!trawlDefaults || typeof trawlDefaults !== 'object') return;
    const d = trawlDefaults.default ?? trawlDefaults;
    if (d.time_of_day != null && d.time_of_day !== '') {
      setTimeOfDayFilter(String(d.time_of_day));
    }
    const depthMin = d.depth_min;
    const depthMax = d.depth_max;
    if (typeof depthMin === 'number' || typeof depthMax === 'number') {
      const min = typeof depthMin === 'number' ? depthMin : 0;
      const max = typeof depthMax === 'number' ? depthMax : 200;
      setDepthRange({ min, max });
      setTempDepthRange({ min, max });
    }
    const botMin = d.bot_temp_min;
    const botMax = d.bot_temp_max;
    if (typeof botMin === 'number' || typeof botMax === 'number') {
      const min = typeof botMin === 'number' ? botMin : -10;
      const max = typeof botMax === 'number' ? botMax : 30;
      setBotTempRange({ min, max });
      setTempBotTempRange({ min, max });
    }
  }, [initialLayers]);

  // Load statistics
  useEffect(() => {
    const loadStatistics = async () => {
      try {
        const response = await getLekStatistics();
        setStatistics(response.data);
      } catch (error) {
        console.error('Statistics fetch failed:', error);
        // Fallback to sample data
        setStatistics({
          total_grid_cells: 140000,
          total_fishermen: 45,
          total_sessions: 180,
          total_polygons: 720,
          species_with_data: 6,
          months_with_data: 12,
          last_updated: new Date().toISOString()
        });
      }
    };
    
    loadStatistics();
  }, [getLekStatistics]);

  // ============================================================================
  // MAP PAGE HANDLERS
  // ============================================================================
  
  // Grid cell selection
  const loadCellDetails = useCallback(async (gridId, speciesId, monthStart, monthEnd) => {
    if (selectedCell?.loading && selectedCell?.gridId === gridId) return;
    
    setSelectedCell({ 
      gridId, 
      loading: true, 
      error: null, 
      details: null 
    });

    console.log('Loading cell details:', { gridId, speciesId, monthStart, monthEnd });

    try {
      const response = await getLekGridCellDetails(gridId, speciesId, monthStart, monthEnd);
      const details = response.data;
      
      console.log('Cell details loaded successfully:', details);
      setSelectedCell({ 
        gridId, 
        loading: false, 
        error: null, 
        details 
      });
    } catch (error) {
      console.error('Failed to load cell details:', error);
      setSelectedCell({ 
        gridId, 
        loading: false, 
        error: error.message, 
        details: null 
      });
    }
  }, [selectedCell, getLekGridCellDetails]);

  const clearSelection = useCallback(() => {
    setSelectedCell(null);
    setMovementAnalysisData(null);
  }, []);

  // 3D Movement Analysis handler
  const handleMovementAnalysis = useCallback((analysisData) => {
    console.log('3D Movement analysis received:', analysisData);
    
    // Store the analysis data
    setMovementAnalysisData(analysisData);
    
    // Show 3D movement popup if we have depth data
    if (analysisData.terrain?.summary?.averageDepth > 0) {
      setActivePopup('3d-movement');
      console.log('3D Movement popup activated with depth:', analysisData.terrain.summary.averageDepth, 'm');
    } else {
      console.warn('No valid water depth found for this location');
    }
  }, []);

  // Unified date range load handler
  const handleUnifiedDateRangeLoad = useCallback(async (speciesId, startDate, endDate) => {
    setCatchabilityLoading(true);
    setDataLoading(true);
    setLekStatus(null);
    
    try {
      console.log('Loading unified data for:', speciesId, startDate, endDate);
      
      // Extract month range from dates
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      const startMonth = startDateObj.getMonth() + 1;
      const endMonth = endDateObj.getMonth() + 1;
      const actualStartMonth = Math.min(startMonth, endMonth);
      const actualEndMonth = Math.max(startMonth, endMonth);
      
      console.log('Date range maps to months:', actualStartMonth, 'to', actualEndMonth);
      
      // Update month range immediately
      setMonthRange({ start: actualStartMonth, end: actualEndMonth });
      
      // Load BOTH catchability AND spatial data simultaneously
      const [catchabilityResult, spatialResult] = await Promise.allSettled([
        // 1. Load temporal catchability data
        getLekCatchabilityData(speciesId, startDate, endDate),
        
        // 2. Load spatial habitat data for the same months
        getLekHabitatData(parseInt(speciesId), { 
          start: actualStartMonth, 
          end: actualEndMonth 
        })
      ]);
      
      // Handle catchability results
      if (catchabilityResult.status === 'fulfilled' && catchabilityResult.value.data?.success) {
        setCatchabilityData(catchabilityResult.value.data);
        console.log('Temporal catchability loaded');
      } else {
        console.warn('Catchability failed:', catchabilityResult.reason);
        setCatchabilityData(null);
      }
      
      // Handle spatial results
      if (spatialResult.status === 'fulfilled') {
        const spatialData = spatialResult.value.data;
        setHabitatData(spatialData);
        console.log('Spatial heatmap loaded:', spatialData?.length || 0, 'cells');
      } else {
        console.warn('Spatial data failed:', spatialResult.reason);
        setHabitatData([]);
      }
      
      // Set unified status
      if (catchabilityResult.status === 'fulfilled' && catchabilityResult.value.data?.success) {
        const catchabilityData = catchabilityResult.value.data;
        setLekStatus({
          type: 'loaded',
          title: 'Combined Data Loaded',
          message: `Temporal: ${catchabilityData.fishermen_count} fishermen responses. Spatial: ${spatialResult.status === 'fulfilled' ? spatialResult.value.data?.length || 0 : 0} grid cells.`,
          details: `Date range: ${startDate} to ${endDate} (months ${actualStartMonth}-${actualEndMonth})`
        });
      } else {
        const speciesName = getSelectedSpeciesNameForMap();
        setLekStatus({
          type: 'error',
          title: 'No Combined Data Available',
          message: `No fishermen knowledge found for ${speciesName}`,
          details: 'Add fishermen responses in the LEK UI first'
        });
      }
      
    } catch (error) {
      console.error('Unified data load error:', error);
      setLekStatus({
        type: 'error',
        title: 'Error Loading Combined Data',
        message: error.message || 'Failed to load temporal and spatial data'
      });
    } finally {
      setCatchabilityLoading(false);
      setDataLoading(false);
    }
  }, [getSelectedSpeciesNameForMap, getLekCatchabilityData, getLekHabitatData]);

  // Popup handlers
  const handleShowCatchabilityResults = useCallback(() => {
    setActivePopup('hourly-catchability');
  }, []);

  const handleShowPortPopup = useCallback((portData) => {
    setSelectedPort(portData);
    setActivePopup('port');
  }, []);

  const handleFleetAnimationPlay = useCallback(() => {
    console.log('🎬 Fleet animation play requested - current trigger:', fleetAnimationTrigger);
    // Note: We can't directly stop scallop fleet here as it's managed in DataLayerTabs
    // The FleetAnimationControls component will handle preventing both from running simultaneously
    setFleetAnimationTrigger(prev => {
      const newTrigger = prev + 1;
      console.log('🎬 New trigger value:', newTrigger);
      return newTrigger;
    });
  }, [fleetAnimationTrigger]);

  const handleFleetAnimationCancel = useCallback(() => {
    console.log('⏹ Fleet animation cancel requested');
    setFleetAnimationTrigger(prev => {
      const newTrigger = prev + 1;
      console.log('⏹ Cancel trigger value:', newTrigger);
      return newTrigger;
    });
  }, []);

  const handleClearPopup = useCallback(() => {
    setActivePopup(null);
    clearSelection();
    setSelectedPort(null);
    setLekPopupScreenPosition(null);
  }, [clearSelection]);

  const handleSwitchToMigrationHeatmap = useCallback(() => setActivePopup('migration-heatmap'), []);
  const handleSwitchToHourlyCatchability = useCallback(() => setActivePopup('hourly-catchability'), []);
  const handleSwitchTo3DMovement = useCallback(() => setActivePopup('3d-movement'), []);
  const handleSwitchToSpeciesLEK = useCallback(() => setActivePopup('species-lek'), []);
  const handleSwitchToPort = useCallback(() => setActivePopup('port'), []);

  // Species change handler
  const handleSpeciesChange = useCallback(async (speciesId) => {
    setSelectedSpecies(speciesId);
    clearSelection();
    setActivePopup(null);
    
    // Load species LEK data via callback (handled by parent component like Stock.jsx)
    if (onSpeciesLEKLoad) {
      onSpeciesLEKLoad(speciesId);
    }
    
    // Clear catchability data when changing species
    setCatchabilityData(null);
    setLekStatus(null);
  }, [clearSelection, onSpeciesLEKLoad]);

  const handleGridCellClick = useCallback((gridId, speciesId, monthStart, monthEnd) => {
    loadCellDetails(gridId, speciesId, monthStart, monthEnd);
    
    // Always show migration heatmap first, but 3D Movement Analysis tab will be available if data exists
    setActivePopup('migration-heatmap');
  }, [loadCellDetails]);

  // LEK grid cell click from MapController (same pattern as ports, cold storage)
  const handleLekGridCellClick = useCallback((gridData) => {
    const speciesId = selectedSpecies || gridData.speciesId;
    if (!speciesId) return;
    loadCellDetails(gridData.gridId, parseInt(speciesId), gridData.monthStart, gridData.monthEnd);
    setActivePopup('migration-heatmap');

    // Store click position for MapController to position popup (same as port/cold storage)
    if (gridData.clickPosition) {
      setLekPopupScreenPosition(gridData.clickPosition);
    }

    // Water column analysis for 3D Movement tab (same as ports/trawl station clicks)
    if (viewer && gridData.latitude != null && gridData.longitude != null) {
      sampleGridTerrain(viewer, gridData.longitude, gridData.latitude, 2)
        .then((terrainData) => {
          handleMovementAnalysis({
            gridId: gridData.gridId,
            gridInfo: {
              latitude: gridData.latitude,
              longitude: gridData.longitude,
              species: gridData.speciesName || 'Unknown',
              species_id: gridData.speciesId,
            },
            terrain: terrainData,
          });
        })
        .catch((err) => console.warn('Water column analysis failed:', err));
    }
  }, [selectedSpecies, loadCellDetails, viewer, handleMovementAnalysis]);

  // Station click handler - loads catchability data for the station's date
  const handleStationClick = useCallback(async (stationData) => {
    console.log('🗺️ Station clicked, loading catchability data:', stationData);

    const dateTimeStr = stationData.dateTime || stationData.begin_est_towdate;
    if (!dateTimeStr || !selectedSpecies) {
      console.warn('Missing station date or species selection');
      return;
    }

    setCatchabilityLoading(true);
    setLekStatus(null);

    try {
      // Extract date from station data
      const stationDate = new Date(dateTimeStr);
      const dateString = stationDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      console.log('Loading catchability for station date:', dateString);
      
      // Load catchability data for the specific date
      const catchabilityResult = await getLekCatchabilityData(parseInt(selectedSpecies), dateString, dateString);
      
      if (catchabilityResult.status === 200 && catchabilityResult.data.success) {
        setCatchabilityData(catchabilityResult.data);
        setActivePopup('hourly-catchability'); // Show catchability tab
        console.log('Station catchability data loaded successfully');
      } else {
        console.warn('Station catchability failed:', catchabilityResult.reason);
        setCatchabilityData(null);
        setLekStatus({
          type: 'error',
          title: 'No Catchability Data',
          message: `No fishermen knowledge found for this station date (${dateString})`,
          details: 'Try selecting a different date range or station'
        });
      }
    } catch (error) {
      console.error('Failed to load station catchability data:', error);
      setCatchabilityData(null);
      setLekStatus({
        type: 'error',
        title: 'Error Loading Catchability',
        message: error.message || 'Failed to load catchability data for this station'
      });
    } finally {
      setCatchabilityLoading(false);
    }
  }, [selectedSpecies, getLekCatchabilityData]);

  const onMonthRangeChange = {
    setStart: useCallback((start) => {
      setMonthRange(prev => ({ ...prev, start }));
    }, []),
    setEnd: useCallback((end) => {
      setMonthRange(prev => ({ ...prev, end }));
    }, [])
  };

  const handleShowMovementAnalysis = (data) => {
    console.log('Show movement analysis:', data);
    setMovementAnalysisData(data);
  };
  
  // ============================================================================
  // MAP PAGE DATA LOADING FUNCTIONS
  // ============================================================================
  
  // Connection check on startup
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await checkLekConnectionHealth();
        setConnectionStatus('connected');
        setConnectionData(response.data);
      } catch (error) {
        console.error('Connection failed:', error);
        setConnectionStatus('error');
      }
    };
    
    checkConnection();
  }, [checkLekConnectionHealth]);

  // Load species data
  useEffect(() => {
    const loadSpecies = async () => {
      setSpeciesLoading(true);
      try {
        const response = await getLekSpecies();
        setSpecies(response.data);
        setSpeciesLoading(false);
      } catch (error) {
        console.error('Species fetch failed:', error);
        // Fallback to sample data
        const fallbackSpecies = [
          { species_id: 1, species_name: 'Atlantic Cod', common_name: 'Atlantic Cod', scientific_name: 'Gadus morhua' },
          { species_id: 2, species_name: 'Haddock', common_name: 'Haddock', scientific_name: 'Melanogrammus aeglefinus' },
          { species_id: 3, species_name: 'American Lobster', common_name: 'American Lobster', scientific_name: 'Homarus americanus' }
        ];
        setSpecies(fallbackSpecies);
        setSpeciesLoading(false);
      }
    };
    
    loadSpecies();
  }, [getLekSpecies]);

  // Session Loading Function
  const loadSessions = useCallback(async () => {
    try {
      setSessionLoading(true);
      setSessionError(null);
      
      const response = await getSessions();
      let sessionsData = [];
      
      if (response?.data?.success && response?.data?.results) {
        sessionsData = response.data.results;
      } else if (Array.isArray(response?.data)) {
        sessionsData = response.data;
      }
      
      setSessions(sessionsData);
      
      // Auto-select the most recent session if available (only on first load)
      setSelectedSessionId(prev => {
        if (prev || sessionsData.length === 0) return prev;
        const mostRecentSession = sessionsData.sort((a, b) => 
          new Date(b.created_date || b.start_date) - new Date(a.created_date || a.start_date)
        )[0];
        return mostRecentSession.session_id;
      });
      
    } catch (err) {
      console.error('Error loading sessions:', err);
      setSessionError(`Failed to load sessions: ${err.message}`);
    } finally {
      setSessionLoading(false);
    }
  }, [getSessions]);

  // Load sessions when component mounts, and when fleet layer is in layout (in case layout loads after mount)
  const hasFleetLayer = initialLayers && (
    ('play-groundfish-simulation' in initialLayers && initialLayers['play-groundfish-simulation'] !== false) ||
    ('play-scallop-simulation' in initialLayers && initialLayers['play-scallop-simulation'] !== false)
  );
  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run on mount
  useEffect(() => {
    if (hasFleetLayer && sessions.length === 0 && !sessionLoading) {
      loadSessions();
    }
  }, [hasFleetLayer, sessions.length, sessionLoading, loadSessions]);

  // Drag handlers for resizing panel
  const handleDragStart = useCallback((e) => {
    setIsDragging(true);
    dragStartY.current = e.clientY || e.touches?.[0]?.clientY;
    dragStartHeight.current = panelHeight;
    e.preventDefault();
    e.stopPropagation();
    // Prevent text selection during drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
  }, [panelHeight]);

  const handleDrag = useCallback((e) => {
    if (dragStartY.current === null || dragStartHeight.current === null) return;
    
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    const deltaY = dragStartY.current - clientY; // Positive when dragging up (increasing height)
    const viewportHeight = window.innerHeight;
    const deltaVh = deltaY / viewportHeight;
    
    // Calculate new height (clamp between 0.1vh and 0.8vh)
    const newHeight = Math.max(0.1, Math.min(0.8, dragStartHeight.current + deltaVh));
    setPanelHeight(newHeight);
  }, []);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    dragStartY.current = null;
    dragStartHeight.current = null;
    // Restore text selection
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);

  // Add global mouse/touch event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDrag);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleDrag);
      document.addEventListener('touchend', handleDragEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleDrag);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleDrag);
        document.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);

  // Create/manage popup container via MapController (consistent with port, cold storage popups).
  // Don't show popup for speciesLEKData alone - that content is in the right panel (lek-species-info).
  const showInfoPanel = activePopup || selectedPort;
  useEffect(() => {
    if (!showInfoPanel || !layerController?.isReady()) return;
    const div = layerController.createPopupContainer(
      lekPopupScreenPosition,
      'lek-info-popup',
      { x: 20, y: 80 }
    );
    setLekPopupContainer(div);
    return () => {
      layerController.removePopupContainer('lek-info-popup');
      setLekPopupContainer(null);
    };
  }, [showInfoPanel, lekPopupScreenPosition, layerController]);

  const infoPanel = lekPopupContainer && showInfoPanel ? (
    createPortal(
      <InfoPanel
        activePopup={activePopup}
        selectedCellDetails={selectedCell}
        selectedPort={selectedPort}
        selectedSpecies={selectedSpecies}
        catchabilityData={catchabilityData}
        speciesLEKData={speciesLEKData}
        movementAnalysisData={movementAnalysisData}
        onClose={handleClearPopup}
        onSwitchToMigrationHeatmap={handleSwitchToMigrationHeatmap}
        onSwitchToHourlyCatchability={handleSwitchToHourlyCatchability}
        onSwitchTo3DMovement={handleSwitchTo3DMovement}
        onSwitchToSpeciesLEK={handleSwitchToSpeciesLEK}
        onSwitchToPort={handleSwitchToPort}
      />,
      lekPopupContainer
    )
  ) : null;

  return (
    <div className={`noscroll-bottom-panel ${visible ? '' : 'hidden'}`}>
      {infoPanel}
      {editMode && (
        <div className="bottom-panel-edit-controls">
          <LayoutEditorControls panelType="bottom" pageType={pageType} resourceId={resourceId} />
        </div>
      )}
      {/* Drag handle for resizing - only visible when a tab is active */}
      {hasActiveTab && (
        <div 
          className={`bottom-panel-resizer ${isDragging ? 'dragging' : ''}`}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        />
      )}
      <ErrorBoundary>
          <DataLayerTabs
            initialLayers={initialLayers}
            species={species}
            selectedSpecies={selectedSpecies}
            onSpeciesChange={handleSpeciesChange}
            speciesLoading={speciesLoading}
            monthRange={monthRange}
            onMonthRangeChange={onMonthRangeChange}
            dataLoading={dataLoading}
            showBottomTemp={showBottomTemp}
            setShowBottomTemp={setShowBottomTemp}
            bottomTempLoading={bottomTempLoading}
            stockSummaryLoading={stockSummaryLoading}
            timeOfDayFilter={timeOfDayFilter}
            setTimeOfDayFilter={setTimeOfDayFilter}
            tempDepthRange={tempDepthRange}
            setTempDepthRange={setTempDepthRange}
            setDepthRange={setDepthRange}
            tempBotTempRange={tempBotTempRange}
            setTempBotTempRange={setTempBotTempRange}
            setBotTempRange={setBotTempRange}
            onPortClick={handleShowPortPopup}
            fleetMapData={fleetMapData}
            fleetDataLoading={fleetDataLoading}
            availableSessions={sessions}
            selectedSessionId={selectedSessionId}
            onSessionChange={(sessionId) => {
              console.log('Session changed:', sessionId);
              setSelectedSessionId(sessionId);
            }}
            onFleetDebugUpdate={(message, data) => console.log('Fleet Debug:', message, data)}
            onFleetAnimationPlay={handleFleetAnimationPlay}
            onFleetAnimationCancel={handleFleetAnimationCancel}
            fleetAnimationTrigger={fleetAnimationTrigger}
            catchabilityLoading={catchabilityLoading}
            onTrawlCatchesChange={setTrawlCatches}
            getTrawlSurveyCruises={getTrawlSurveyCruises}
            getTrawlSurveyCatchesByCruise={getTrawlSurveyCatchesByCruise}
            showPorts={showPorts}
            setShowPorts={setShowPorts}
            showColdStorage={showColdStorage}
            setShowColdStorage={setShowColdStorage}
            onColdStorageClick={(facility) => {
              console.log('Cold storage facility clicked:', facility);
              // Could show a popup or update state here if needed
            }}
            showStateBoundaries={showStateBoundaries}
            setShowStateBoundaries={setShowStateBoundaries}
            onSpeciesLEKLoad={onSpeciesLEKLoad}
            onToggleSpeciesLEKData={onToggleSpeciesLEKData}
            onLekGridCellClick={handleLekGridCellClick}
            onMapEmptyClick={clearSelection}
            onTrawlSurveyStationClick={handleStationClick}
            panelHeight={panelHeight}
            onActiveTabChange={setHasActiveTab}
          />
      </ErrorBoundary>
    </div>
  );
};

export default BottomPanel;
